import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { extractError } from '@/lib/errors';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { FileText, Building2, Download } from 'lucide-react';

const PREF_KEY = 'pdf.include_branding';

/**
 * PdfDownloadDialog — Phase 11
 *
 * Reusable dialog presented before any PDF/Excel download. Asks the user
 * whether to print the company logo and name on the document. The preference
 * is remembered in localStorage so the default reflects the user's last
 * choice, but the user can flip it on every download.
 *
 * Props:
 *  - open / onOpenChange    standard
 *  - title                  Display title (e.g. "Download Issue Memo PDF")
 *  - description            Short hint shown under the title
 *  - companyId              UUID of the company whose branding will be used
 *  - endpoint               Backend URL path (no host); e.g. `/goldsmith-issues/{id}/pdf`.
 *                           The dialog adds `?include_branding=true|false` (plus any extra query params).
 *  - filename               Suggested download filename
 *  - extraParams            Optional dict of additional query params
 *  - inline                 If true, opens the PDF in a new tab; otherwise downloads.
 *  - mode                   'download' (default) | 'view' | 'print'
 *  - extras                 Optional React node to render BELOW the branding
 *                           card. Useful for PDF-specific toggles such as
 *                           "Raw material ledger: Collapsed / Expanded" on
 *                           the BOM. The parent owns the toggle state and
 *                           is responsible for reflecting it in ``extraParams``.
 */
export function PdfDownloadDialog({
  open, onOpenChange, title = 'Download PDF', description,
  companyId, endpoint, filename, extraParams = {}, inline = false,
  mode = 'download', extras = null,
}) {
  // Default to last user preference, fallback true.
  const initialPref = (() => {
    try {
      const v = localStorage.getItem(PREF_KEY);
      if (v === '0') return false;
      if (v === '1') return true;
    } catch { /* noop */ }
    return true;
  })();
  const [includeBranding, setIncludeBranding] = useState(initialPref);
  const [company, setCompany] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!open || !companyId) { setCompany(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/companies/${companyId}/logo`);
        if (!cancelled) setCompany(data);
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [open, companyId]);

  // Persist user pref
  useEffect(() => {
    try { localStorage.setItem(PREF_KEY, includeBranding ? '1' : '0'); } catch { /* noop */ }
  }, [includeBranding]);

  const submit = async () => {
    if (!endpoint) { toast.error('Missing endpoint'); return; }
    setDownloading(true);
    try {
      const params = { ...extraParams, include_branding: includeBranding };
      const resp = await api.get(endpoint, { params, responseType: 'blob' });
      const blob = new Blob([resp.data], { type: resp.headers['content-type'] || 'application/pdf' });
      const url = URL.createObjectURL(blob);
      if (mode === 'view') {
        const w = window.open(url, '_blank', 'noopener,noreferrer');
        if (!w) toast.error('Browser blocked the popup. Please allow popups and try again.');
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } else if (mode === 'print') {
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed'; iframe.style.right = '0'; iframe.style.bottom = '0';
        iframe.style.width = '0'; iframe.style.height = '0'; iframe.style.border = '0';
        iframe.src = url;
        iframe.onload = () => {
          try { iframe.contentWindow?.focus(); iframe.contentWindow?.print(); }
          catch { toast.error('Unable to open print dialog. Use Download instead.'); }
          setTimeout(() => { iframe.remove(); URL.revokeObjectURL(url); }, 60_000);
        };
        document.body.appendChild(iframe);
      } else {
        // download (default)
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || 'document.pdf';
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      }
      onOpenChange(false);
    } catch (e) {
      toast.error(extractError(e, 'Operation failed'));
    } finally {
      setDownloading(false);
    }
  };

  const submitLabel = mode === 'view'
    ? 'Open PDF'
    : mode === 'print'
    ? 'Print PDF'
    : 'Download PDF';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="pdf-download-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileText size={16} /> {title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <Card className="p-3 bg-[var(--paper-warm)] border-[var(--divider)]">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              {company?.has_logo && company?.logo_data_url ? (
                <img src={company.logo_data_url} alt="" className="w-10 h-10 rounded object-contain bg-white border border-[var(--divider)]" />
              ) : (
                <div className="w-10 h-10 rounded bg-white border border-[var(--divider)] flex items-center justify-center text-slate-400">
                  <Building2 size={16} />
                </div>
              )}
              <div className="min-w-0">
                <div className="text-sm font-medium text-[var(--ink-navy)] truncate">{company?.name || 'Company'}</div>
                <div className="text-[11px] text-slate-500">{company?.has_logo ? 'Logo & name available' : 'No logo uploaded — only the name can appear'}</div>
              </div>
            </div>
            <Badge className="text-[10px] bg-blue-50 text-blue-700">Branding</Badge>
          </div>
          <label className="flex items-start gap-3 mt-3 cursor-pointer select-none" data-testid="pdf-include-branding-label">
            <Checkbox
              checked={includeBranding}
              onCheckedChange={(v) => setIncludeBranding(!!v)}
              data-testid="pdf-include-branding-checkbox"
              className="mt-0.5"
            />
            <div>
              <div className="text-sm text-[var(--ink-navy)]">Include company logo / name on this PDF</div>
              <div className="text-[11px] text-slate-500">
                {includeBranding
                  ? (company?.has_logo ? 'Logo and company name will appear in the document header.' : 'Only the company name will appear (no logo has been uploaded).')
                  : 'The company logo and name will be hidden on this document.'}
              </div>
            </div>
          </label>
        </Card>
        {extras ? (
          <div className="mt-3" data-testid="pdf-dialog-extras-slot">
            {extras}
          </div>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="luxury-button" disabled={downloading} data-testid="pdf-download-confirm-button">
            <Download size={14} className="mr-1.5" /> {downloading ? 'Preparing…' : submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default PdfDownloadDialog;
